/**
 * @render react
 * @name MOTChecker
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { withPolaris } from 'Components/protons/Polaris';
import { NumberPlate } from 'Components/atoms/NumberPlate';
import { Button } from 'Components/atoms/Button';
import { CalendarReminder } from 'Components/molecules/CalendarReminder';
import { Heading } from 'Components/molecules/Heading';
import { Link } from 'Components/molecules/Link';
import { RegPlateForm } from 'Components/molecules/RegPlateForm';
import { StatusIndicatorBadge } from 'Components/molecules/StatusIndicatorBadge';
import { Accordion } from 'Components/organisms/Accordion';
import { Tooltip } from 'Components/molecules/Tooltip';
import { Icon } from 'Components/atoms/Icon';

import './_style.scss';

import { getMOTCheckerData, getTaxCheckerData } from './data';

/**
 * MOTChecker component
 * @param {Object} props - The properties object.
 * @param {string} props.checkerType - The type of checker to use e.g. MOT_CHECKER or TAX_CHECKER.
 * @param {Array} props.children - The child elements to be rendered inside the component.
 * @param {Object} props.context - The context object containing additional information.
 * @param {string} props.context.cssPrefix - The CSS prefix for styling.
 * @param {string} props.subtitle - The subtitle for the MOTChecker component.
 * @param {string} props.title - The title for the MOTChecker component.
 * @param {string} props.tooltipCloseIcon - The icon to use for the Tooltip close button.
 * @returns {React.ReactElement} - The rendered MOTChecker component.
 */
const MOTChecker = ({
  checkerType,
  children = [],
  context: { cssPrefix },
  iconClose,
  iconOpen,
  subtitle,
  title,
  tooltipCloseIcon,
}) => {
  /**
   * Initial state for the MOTChecker component.
   */
  const initialState = ({
    classSelector: `${cssPrefix}__motchecker--step-1`,
    isStepOne: true,
    title,
    vrm: '',
    motStatusIndicatorBadge: null,
    taxStatusIndicatorBadge: null,
    motExpiryDate: null,
    taxDueDate: null,
    motOdometerValue: null,
    primaryColour: null,
    fuelType: null,
    registrationDate: null,
    hasOutstandingRecall: null,
    make: '',
    model: '',
  });

  const [state, setState] = useState(initialState);

  const numberOfDaysBeforeToRemind = 14;
  const getData = (checkType) => (
    checkType === 'MOT_CHECKER'
      ? getMOTCheckerData(numberOfDaysBeforeToRemind)
      : getTaxCheckerData(numberOfDaysBeforeToRemind)
  );

  /**
   * Generates a StatusIndicatorBadge component based on the provided status, type, and latest test result.
   *
   * @param {string} status - The status of the indicator (e.g., 'Valid', 'PASSED', 'FAILED').
   * @param {string} type - The type of the indicator (e.g., 'mot', 'tax').
   * @param {Object} [latestTestResult] - The latest test result object, if available.
   * @param {string} [latestTestResult.expiryDate] - The expiry date of the latest test result.
   * @param {string} [latestTestResult.testResult] - The result of the latest test (e.g., 'PASSED', 'FAILED').
   * @returns {React.ReactElement|null} - The StatusIndicatorBadge component or null if no status information is available.
   */
  const getStatusIndicatorBadge = (status, type, latestTestResult) => {
    const isExpired = (expiryDate) => expiryDate && new Date(expiryDate) < new Date();
    const createStatusInfo = (statusIndicator, statusValue, icon, text) => ({
      status: statusIndicator, statusValue, icon, text,
    });

    const statusMaps = {
      mot: {
        'No details held by DVLA': createStatusInfo('warning', 'No details held by DVLA', 'ico-exclamation-mark', 'No details held by DVLA'),
        'No results returned': createStatusInfo('warning', 'No results returned', 'ico-exclamation-mark', 'No results returned'),
        Valid: createStatusInfo('success', 'Passed', 'ico-check-2', 'MOT'),
        PASSED: isExpired(latestTestResult?.expiryDate)
          ? createStatusInfo('error', 'Expired', 'ico-close-2', 'MOT Expired')
          : createStatusInfo('success', 'Passed', 'ico-check-2', 'MOT'),
        FAILED: createStatusInfo('error', 'Failed', 'ico-close-2', 'MOT Failed'),
        'Not valid': (() => {
          if (latestTestResult?.testResult === 'FAILED') {
            return createStatusInfo('error', 'Failed', 'ico-close-2', 'MOT Failed');
          }
          if (latestTestResult?.testResult === 'PASSED' && isExpired(latestTestResult?.expiryDate)) {
            return createStatusInfo('error', 'Expired', 'ico-close-2', 'MOT Expired');
          }
          return null;
        })(),
      },
      tax: {
        'Not Taxed for on Road Use': createStatusInfo('warning', 'Not Taxed for on Road Use', 'ico-exclamation-mark', 'Not Taxed for on Road Use'),
        SORN: createStatusInfo('warning', 'SORN', 'ico-exclamation-mark', 'SORN'),
        Taxed: createStatusInfo('success', 'Taxed', 'ico-check-2', 'Taxed'),
        Untaxed: createStatusInfo('error', 'Untaxed', 'ico-close-2', 'Untaxed'),
        null: createStatusInfo('warning', 'Vehicle not found', 'ico-exclamation-mark', 'Vehicle not found'),
        404: createStatusInfo('warning', 'Vehicle not found', 'ico-exclamation-mark', 'Vehicle not found'),
      },
    };

    const statusInfo = statusMaps[type]?.[status];

    let formattedStatusValue;
    switch (type) {
      case 'mot':
        formattedStatusValue = `MOT status: ${statusInfo?.statusValue}`;
        break;
      case 'tax':
        formattedStatusValue = `Tax status: ${statusInfo?.statusValue}`;
        break;
      default:
        formattedStatusValue = `${type} status: ${statusInfo?.statusValue}`;
    }

    return statusInfo
      ? (
        <StatusIndicatorBadge
          {...statusInfo}
          statusValue={formattedStatusValue}
          extraClassNames={{
            [`${cssPrefix}__motchecker--status-indicator-badge`]: true,
            [`-${type}`]: true,
          }}
        />
      )
      : null;
  };

  const submitVRM = (data) => {
    const {
      fuelType,
      hasOutstandingRecall,
      make,
      model,
      motTests,
      primaryColour,
      registration,
      registrationDate,
    } = data.vehicleDataItems.mot;

    // Get the latest test result from the motTests array
    const latestTestResult = motTests?.[0];
    //  Get the tax status from the data.vehicleDataItems.tax
    const taxStatus = data?.vehicleDataItems?.tax?.taxStatus || data?.vehicleDataItems?.tax?.errors?.[0]?.status || null;
    const taxDueDate = data?.vehicleDataItems?.tax?.taxDueDate || null;
    // Get the mot status from the data.vehicleDataItems.tax, or if not available, from the latest test result
    const motStatus = data?.vehicleDataItems?.tax?.motStatus || (data?.vehicleDataItems?.tax?.errors?.[0]?.status === '404' && latestTestResult?.testResult) || null;

    setState((prevState) => ({
      ...prevState,
      isStepOne: false,
      classSelector: `${cssPrefix}__motchecker--step-2`,
      title: `${make || ''} ${model || ''}`,
      vrm: registration,
      motStatusIndicatorBadge: motStatus ? getStatusIndicatorBadge(motStatus, 'mot', latestTestResult) : null,
      taxStatusIndicatorBadge: getStatusIndicatorBadge(taxStatus, 'tax'),
      taxStatus,
      taxDueDate,
      motExpiryDate: data?.vehicleDataItems?.tax?.motExpiryDate || latestTestResult?.expiryDate || null,
      motOdometerValue: latestTestResult?.odometerValue && !Number.isNaN(latestTestResult.odometerValue)
        ? `${Intl.NumberFormat('en-GB').format(latestTestResult.odometerValue)} miles`
        : null,
      primaryColour: primaryColour || '-',
      fuelType: fuelType || '-',
      registrationDate: registrationDate || null,
      hasOutstandingRecall: hasOutstandingRecall || null,
      motTests: motTests || [],
      make: make || '',
      model: model || '',
    }));
  };

  const handleBackButtonClick = () => {
    setState(initialState);

    // Clear the VRM from session storage to reset the form state
    // This ensures the input field is empty when returning to step 1
    sessionStorage.removeItem('vrm');
  };

  const backButtonBlock = (onClick) => (
    <Button
      type="button"
      onClick={onClick}
      extraClassNames={{ [`${cssPrefix}__motchecker--back-button`]: true }}
      label="Check another vehicle"
      variant="-link"
    />
  );

  const formattedDate = (date) => {
    if (!date) {
      return '-';
    }
    const parsed = new Date(date);
    return date && parsed instanceof Date && !Number.isNaN(parsed.getTime())
      ? parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
      : '-';
  };

  const regPlateElement = state.isStepOne ? (
    <RegPlateForm
      ctaLabel={`Check ${checkerType === 'MOT_CHECKER' ? 'MOT' : 'Tax'}`}
      inputPlaceholder="Enter reg"
      queryType="mot,tax"
      trackingData={getData(checkerType).regPlateTrackingData}
      submitAction={submitVRM}
      submitTypeIsButton
      extraClassNames={{ [`${cssPrefix}__motchecker--regplateform`]: true }}
    />
  ) : null;

  const subtitleElement = state.isStepOne ? (
    <p className={`${cssPrefix}__motchecker--subtitle`}>{subtitle}</p>
  ) : null;

  const disclaimerElement = state.isStepOne && checkerType === 'MOT_CHECKER' ? (
    <p className={`${cssPrefix}__motchecker--disclaimer`}>
      {
        'MOT history is only available for tests done in England, Scotland or '
        + 'Wales since 2005, or for tests done in Northern Ireland since 2017. '
        + 'A vehicle’s MOT test results will be available as soon as the MOT centre '
        + 'has recorded the test.'
      }
    </p>
  ) : null;

  const backButtonElement = !state.isStepOne ? backButtonBlock(handleBackButtonClick) : null;

  const identifiedRegPlateElement = !state.isStepOne && state.vrm ? (
    <NumberPlate vrm={state.vrm} />
  ) : null;

  const motStatusIndicatorBadge = !state.isStepOne && state.vrm
    ? state.motStatusIndicatorBadge
    : null;

  const taxStatusIndicatorBadge = !state.isStepOne && state.vrm
    ? state.taxStatusIndicatorBadge
    : null;

  const motOdometerValue = !state.isStepOne && state.vrm && state.motOdometerValue
    ? (
      <StatusIndicatorBadge
        extraClassNames={{ [`${cssPrefix}__motchecker--mot-tests--info`]: true }}
        text={state.motOdometerValue}
        status="info"
        statusValue={state.motOdometerValue}
        icon="ico-speedometer"
      />
    )
    : null;

  /**
   * Renders expiry information for MOT or Tax based on the check type
   * @param {string} checkType - The type of check being performed ('MOT_CHECKER' or 'TAX_CHECKER')
   * @returns {JSX.Element|null} A paragraph element containing expiry information if conditions are met, null otherwise
   */
  const getExpiryDate = (checkType) => (!state.isStepOne && state.vrm ? (
    <p className={`${cssPrefix}__motchecker--expiry-information`}>
      {`${checkType === 'MOT_CHECKER' ? 'MOT' : 'Tax'} valid until`}
      {' '}
      <strong>{formattedDate(checkType === 'MOT_CHECKER' ? state?.motExpiryDate : state?.taxDueDate)}</strong>
    </p>
  ) : null);

  let reminderDate;
  if (!state.isStepOne) {
    reminderDate = new Date(checkerType === 'MOT_CHECKER' ? state?.motExpiryDate : state?.taxDueDate);
    reminderDate.setDate(reminderDate.getDate() - numberOfDaysBeforeToRemind);
  }

  const getCalendarReminder = (checkType) => !state.isStepOne && (
    <CalendarReminder
      dateToRemind={reminderDate}
      onClick={() => {
        window.dataLayer?.push({
          event: getData(checkType).reminder.trackingData.event,
        });
      }}
      {...getData(checkType).reminder}
    />
  );

  /**
   * Generates a contact link component based on the checker type.
   *
   * @param {string} checkType - The type of checker ('MOT_CHECKER' or 'TAX_CHECKER')
   * @returns {React.ReactElement|null} - Returns a paragraph element with a contact link if not in step one,
   *                                     otherwise returns null
   *
   * The function:
   * - Returns null if in step one
   * - Falls back to MOT_CHECKER values if invalid checkType provided
   * - Returns a paragraph containing explanatory text and a Link component
   */
  const getContactLink = (checkType) => {
    if (state.isStepOne) {
      return null;
    }

    const { text, href, agency } = getData(checkType).contact || getData('MOT_CHECKER').contact;

    return (
      <p className={`${cssPrefix}__motchecker--contact-dvsa`}>
        {text}
        <Link href={href}>
          {`contact ${agency}`}
        </Link>
      </p>
    );
  };

  const motTestsSummary = !state.isStepOne && (
  <div className={`${cssPrefix}__motchecker--mot-tests--summary`}>
    {checkerType === 'TAX_CHECKER' ? taxStatusIndicatorBadge : motStatusIndicatorBadge}
    {checkerType === 'TAX_CHECKER' ? motStatusIndicatorBadge : taxStatusIndicatorBadge}
    {motOdometerValue}
    {getExpiryDate(checkerType)}
    {getCalendarReminder(checkerType)}
    {getContactLink(checkerType)}
  </div>
  );

  const detailsTitleComponent = (detailsTitle, detailsSubtitle) => (
    <>
      {detailsTitle && (<p className={`${cssPrefix}__motchecker--accordion--item-title`}>{detailsTitle}</p>)}
      {detailsSubtitle && (<p className={`${cssPrefix}__motchecker--accordion--item-subtitle`}>{detailsSubtitle}</p>)}
    </>
  );

  // Set the recall states.
  const RECALL_STATES = {
    NO: 'No',
    YES: 'Yes',
    UNKNOWN: 'Unknown',
  };

  const recallMessages = {
    [RECALL_STATES.NO]: {
      recallText: 'No - There were one or more recalls which have all been fixed',
      recallColor: 'black',
    },
    [RECALL_STATES.YES]: {
      recallText: 'Yes - There is at least one recall which has not yet been fixed',
      recallColor: 'black',
    },
    [RECALL_STATES.UNKNOWN]: {
      recallText: 'No outstanding recalls found',
      recallColor: 'black',
    },
    default: {
      recallText: 'Unavailable - We were unable to retrieve data from the Recalls service due to an error',
      recallColor: 'red',
    },
  };

  const getRecallMessage = (hasOutstandingRecall) => recallMessages[hasOutstandingRecall] || recallMessages.default;

  const recallsContent = () => {
    const { hasOutstandingRecall } = state;
    const { recallText, recallColor } = getRecallMessage(hasOutstandingRecall);

    return (
      <div className={`${cssPrefix}__motchecker--recalls-content`}>
        <p className={`${cssPrefix}__motchecker--recalls-${recallColor}`}>
          <strong>{recallText}</strong>
        </p>
        <p>
          Visit this page for details of any recalls on your vehicle:
          {' '}
          <a
            href="https://www.check-vehicle-recalls.service.gov.uk/recall-type/vehicle/make"
            target="_blank"
            rel="noreferrer"
          >
            https://www.check-vehicle-recalls.service.gov.uk/recall-type/vehicle/make
          </a>
        </p>
      </div>
    );
  };

  const defectsComponent = (defects, headline, tooltip) => {
    if (!defects || defects.length === 0) {
      return null;
    }

    return (
      <div className={`${cssPrefix}__motchecker--test-history-defects`}>
        <span>{headline}</span>
        <Tooltip
          icon="polaris__ico-info"
          content={tooltip}
          closeLabel={<Icon icon={tooltipCloseIcon} />}
        />
        <ul>
          {defects.map(({ type, text }, index) => (
            <li key={`${type}_${index}`}>
              <p>{text}</p>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const getDefectsOfType = (defects, type) => defects?.filter((defect) => defect?.type === type) ?? [];

  const testResultComponent = (test) => {
    const {
      completedDate, odometerValue, motTestNumber, testResult, expiryDate,
    } = test;

    const labelStyle = `${cssPrefix}__motchecker--test-history-list-label`;
    const textStyle = `${cssPrefix}__motchecker--test-history-list-text`;
    const textStylePassed = `${cssPrefix}__motchecker--test-history-list-text-passed`;
    const textStyleFailed = `${cssPrefix}__motchecker--test-history-list-text-failed`;

    const advisories = getDefectsOfType(test?.defects, 'ADVISORY');
    const dangerous = getDefectsOfType(test?.defects, 'DANGEROUS');
    const major = getDefectsOfType(test?.defects, 'MAJOR');
    const minor = getDefectsOfType(test?.defects, 'MINOR');

    return (
      <div className={`${cssPrefix}__motchecker--test-history-card`}>
        <ul className={`${cssPrefix}__motchecker--test-history-list`}>
          <li>
            <p className={labelStyle}>Date tested</p>
            <p className={textStyle}>{formattedDate(completedDate)}</p>
          </li>
          <li className={testResult === 'PASSED' ? textStylePassed : textStyleFailed}>
            <p className={`${cssPrefix}__motchecker--test-history-list-text-result-content`}>{testResult}</p>
          </li>
          <li>
            <p className={labelStyle}>Mileage</p>
            <p className={textStyle}>{Intl.NumberFormat('en-GB').format(odometerValue)}</p>
          </li>
          <li>
            <p className={labelStyle}>Expiry date</p>
            <p className={textStyle}>{formattedDate(expiryDate)}</p>
          </li>
          <li>
            <p className={labelStyle}>MOT test number</p>
            <p className={textStyle}>{motTestNumber}</p>
          </li>
        </ul>
        {defectsComponent(
          dangerous,
          'Do not drive until repaired (dangerous defects):',
          'Dangerous defects are a direct and immediate risk to road safety or the environment.',
        )}
        {defectsComponent(
          major,
          'Repair immediately (major defects):',
          'Major defects may compromise safety, put other road users at risk, or harm the environment. ',
        )}
        {defectsComponent(
          minor,
          'Repair as soon as possible (minor defects):',
          'Minor defects have no significant effect on the safety of the vehicle or impact on the environment.',
        )}
        {defectsComponent(
          advisories,
          'Monitor and repair if necessary (advisories):',
          'Advisories are given for guidance. Some  may need to be monitored.',
        )}
      </div>
    );
  };

  const motAndRecallDetails = !state.isStepOne ? (
    <Accordion
      items={[{
        titleComponent: detailsTitleComponent(
          'View full MOT history',
          'Check MOT expiry date, mileage recorded at test, defects and advisories.',
        ),
        content: (
          <div className={`${cssPrefix}__motchecker--test-history-info`}>
            {state.motTests.map((test, index) => (
              <React.Fragment key={test.motTestNumber}>
                {testResultComponent(test, `testHistory${index}`)}
              </React.Fragment>
            ))}
          </div>),
      },
      {
        titleComponent: detailsTitleComponent(
          'Outstanding vehicle recalls',
          `Check if ${state.title} (${state.vrm}) has outstanding recalls.`,
        ),
        content: recallsContent(),
      }]}
      exclusive={false}
      firstIsOpen={false}
      iconClose={iconClose}
      iconOpen={iconOpen}
      extraClassNames={{
        [`${cssPrefix}__motchecker--accordion`]: true,
      }}
    />
  ) : null;

  /**
   * Renders a heading followed by a list of key-value pairs as vehicle information
   * @param {string} heading - The title to be displayed above the list
   * @param {Object.<string, string>} keyValuePairs - An object containing label-text pairs to be displayed in the list
   * @returns {JSX.Element} React fragment containing a heading and an unordered list of vehicle information
   */
  const vehicleInfoList = (heading, keyValuePairs) => (
    <>
      <Heading size={4} extraClassNames={{ [`${cssPrefix}__motchecker--vehicle-info-header`]: true }}>
        {heading}
      </Heading>
      <ul className={`${cssPrefix}__motchecker--vehicle-info-list`}>
        {Object.entries(keyValuePairs).map(([label, text]) => (
          <li key={label}>
            <p className={`${cssPrefix}__motchecker--vehicle-info-list-label`}>{label}</p>
            <p className={`${cssPrefix}__motchecker--vehicle-info-list-text`}>{text}</p>
          </li>
        ))}
      </ul>
    </>
  );

  const formattedRegistrationDate = formattedDate(state?.registrationDate);
  const vehicleInformationData = {
    Colour: state.primaryColour,
    'Fuel type': state.fuelType,
    'Date registered': formattedRegistrationDate,
  };

  const formattedTaxDueDate = formattedDate(state?.taxDueDate);
  const taxData = {
    'Tax status': state?.taxStatus === '404' ? '-' : (state?.taxStatus || '-'),
    'Tax due date': formattedTaxDueDate,
  };

  const vehicleInformation = !state.isStepOne && vehicleInformationData ? (
    <div className={`${cssPrefix}__motchecker--vehicle-info`}>
      {vehicleInfoList('Vehicle information', vehicleInformationData)}
      <div className={`${cssPrefix}__motchecker--vehicle-info-line`} />
      {vehicleInfoList('Tax information', taxData)}
    </div>
  ) : null;

  return (
    <>
      <div className={`${cssPrefix}__motchecker--header`}>
        <div className={`${cssPrefix}__motchecker--header-content ${state.classSelector}`}>
          {backButtonElement}
          <Heading size={1} extraClassNames={{ [`${cssPrefix}__motchecker--title`]: true }}>
            {state.title}
          </Heading>
          {subtitleElement}
          {regPlateElement}
          {identifiedRegPlateElement}
          {disclaimerElement}
        </div>
      </div>
      <div className={`${cssPrefix}__motchecker--content`}>
        {motTestsSummary}
        {checkerType === 'TAX_CHECKER' && vehicleInformation}
        {motAndRecallDetails}
        {checkerType === 'MOT_CHECKER' && vehicleInformation}
        {state.isStepOne && children}
      </div>
    </>
  );
};

/**
 * Valid props
 */
MOTChecker.propTypes = {
  checkerType: PropTypes.string,
  children: PropTypes.arrayOf(PropTypes.element),
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
  iconClose: PropTypes.string,
  iconOpen: PropTypes.string,
  subtitle: PropTypes.string,
  title: PropTypes.string,
  tooltipCloseIcon: PropTypes.string,
};

/**
 * Default props
 */
MOTChecker.defaultProps = {
  checkerType: 'MOT_CHECKER',
  children: [],
  context: {
    cssPrefix: 'polaris',
  },
  iconClose: 'polaris__ico-arrow_up',
  iconOpen: 'polaris__ico-arrow_down',
  subtitle: '',
  title: 'MOT Checker',
  tooltipCloseIcon: 'polaris__ico-close',
};

export default withPolaris(MOTChecker);
